import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Plus, RefreshCw, X } from 'lucide-react';
import ClientCommandBar from './partials/ClientCommandBar';
import ClientAddFab from './partials/ClientAddFab';
import ClientDetailDrawer from './partials/ClientDetailDrawer';
import ClientPagination from './partials/ClientPagination';
import ClientRow from './partials/ClientRow';
import ClientTabs from './partials/ClientTabs';
import SmartFilterSidebar from './partials/SmartFilterSidebar';
import SoftDepthScope from '../../../design-system/SoftDepthScope';
import StatTiles from './partials/StatTiles';
import RecruitSuggestions from './partials/RecruitSuggestions';
import RecruitModeWipe from './partials/RecruitModeWipe';
import RecruitStageMenu from './partials/RecruitStageMenu';
import TodayQueue from './partials/TodayQueue';
import { buildTodayQueue } from '../../../utils/client-queue';
import { QUEUE_PREVIEW_SIZE } from '../../../constants/client-queue';
import { getClientAnalysisScores } from '../../../utils/client';
import {
  clientBaseLevelOf,
  clientStatsOf,
  filterClientsByStat,
  filterClientsByTab,
  sortClients,
  tabCountsOf
} from './clientListView';
import type { ClientTierChoice, ExistingClientChoice, IntlLike } from './clientPresentation';
import type { ClientSort, ClientStatKey, ClientTab } from './clientListView';
import type { ClientDrawerAction } from './clientActions';
import {
  filterRecruitsByStat,
  filterRecruitsByTab,
  recruitStatsOf,
  recruitTabCountsOf,
  sortAndFilterRecruitsBySort,
  useRecruitFacts
} from './recruitListView';
import type { RecruitSort, RecruitStatKey, RecruitTab } from './recruitListView';
import { readRecruitMode, rememberRecruitView, writeRecruitMode } from './clientViewState';
import { writeRecruitStage } from './recruitStage';
import { buildRecruitSuggestions } from './recruitSuggestions';
import { pickFactsFillIn, readFillInSnoozes, snoozeFillIn } from './factsFillIn';
import type { FactsFillIn } from './factsFillIn';
import {
  RECRUIT_APPLY_AT_MS,
  RECRUIT_TRANSITION_MS,
  prefersReducedMotion,
  pulse,
  wipeOriginOf
} from './recruitModeTransition';
import type { WipeOrigin } from './recruitModeTransition';
import type { RecruitSuggestion } from './recruitSuggestions';
import type { QuickFactKey } from './partials/ClientDetailDrawer';
import type { ClientCapability } from './clientEntitlements';
import {
  clientFilterOptionsOf,
  createDefaultClientFilters,
  filterClients,
  hasActiveClientFilters,
  migrateFamilyFilter
} from './clientFilters';
import { filterClientsBySearch } from './clientSearch';
import {
  LEGACY_STATE_LABEL_KEYS,
  POLICY_GAP_STATES,
  filterClientsByLegacyState,
  movePolicyTargetErrorOf
} from './clientLegacyState';
import { CLIENT_PAGE_SIZE, clampPage, paginateItems } from './clientPagination';
import {
  clearClientFormDrafts,
  clearLegacyClientSort,
  clearPersistedClient,
  readPersistedClientView,
  writePersistedClientView
} from './clientViewState';
import type { ClientListMode, PersistedClientView } from './clientViewState';
import type { CalendarFilter } from './reminderCalendar';
import { reminderCalendarMonth } from './reminderCalendar';
import ReminderCalendar from './partials/ReminderCalendar';
import ReminderDayList from './partials/ReminderDayList';
import { useSeedClientNotes } from './clientNotesStore';
import { qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import type { AnalysisScores } from '../../../utils/client-insights';
import type { ClientTag, PolicyDueEntry, QueueClient } from '../../../types/client';
import type { ClientFilters } from './clientFilters';
import type { ClientLegacyState, ClientLegacyStateRequest, PastBirthdayUser, PolicyGapData } from './clientLegacyState';
import './styles.scss';

// How often the page checks whether the calendar day has turned (see
// `liveNow`). A minute is far finer than the thing being watched, which is the
// point: the check is one `moment()` and a comparison, and it costs a render
// only on the one tick a day that actually changes the answer.
const DAY_ROLLOVER_CHECK_MS = 60 * 1000;

// How long a newly added client's row keeps its `is-spotlight` flash — the
// CSS (styles.scss `.clientv2-row.is-spotlight`) runs the pulses for 0.6s of
// delay (the row's rise-in plays first) + 2.3s of flash, so the class has to
// outlive both. Clearing it returns the row to its plain borders; nothing
// else reads the class.
const SPOTLIGHT_MS = 3000;

export type ClientV2Props = {
  clientData: QueueClient[];
  policyDue: PolicyDueEntry[];
  policiesData?: PolicyGapData;
  connectedUserPolicies?: () => void;
  pastBirthdayUsers?: PastBirthdayUser[];
  getAdviserDashboard?: () => void;
  userEmail?: string;
  /**
   * The adviser's own record. Only `personality` is read here: the DISC axis of
   * the C.P.I. scores nothing unless BOTH letters are set (server
   * utils/client.js), so the 一格解鎖 tile must not offer a client's DISC to an
   * adviser who has never set their own.
   */
  userDetails?: { name?: string; personality?: string | null };
  currency?: string;
  figure?: unknown;
  families?: string[];
  usedTags?: ClientTag[];
  now?: moment.MomentInput;
  intl?: IntlLike;
  /**
   * React Router location, forwarded by the bridge. Only `select` and
   * `preSelectedClientIds` are read here — a deep link overrides the restored
   * position, exactly as it does in V1 (clientPage.js:441-455).
   */
  location?: { select?: unknown; preSelectedClientIds?: unknown };
  getClient?: () => void;
  /** True while a GET_CLIENT is in flight — drives the refresh control and
   * stops a mount from stacking a second fetch on login/boot's own. */
  fetchingClients?: boolean;
  fetchFigureAdviser?: () => void;
  /** Refreshes the adviser record the family palette, tag palette and
   * subscription gate all read (V1 does this on mount, clientPage.js:422). */
  getUser?: () => void;
  /** Resets `user.approvePhase` on the way out, as V1's unmount does. */
  initPhaseEmail?: () => void;
  onConnect?: (client: QueueClient) => void;
  onBirthday?: (client: QueueClient) => void;
  onEditPersonality?: (client: QueueClient) => void;
  onEditFamily?: (client: QueueClient) => void;
  onEditTags?: (client: QueueClient) => void;
  onEditExisting?: (client: QueueClient, value: ExistingClientChoice | null) => void;
  onEditTier?: (client: QueueClient, value: ClientTierChoice | null) => void;
  /** 補資料 — one C.P.I. factsheet field, written from the drawer popup. */
  onEditFact?: (client: QueueClient, key: QuickFactKey, value: string | number | boolean | null) => void;
  /**
   * Bumped by the Bridge each time a factsheet write comes back failed. The
   * drawer echoes its answers optimistically, so this is what tells it to stop:
   * on a change it drops every echo and falls back to the store.
   */
  factWriteFailed?: number;
  /** `pendingFactWrites > 0` — see ClientDetailDrawer's own prop. */
  factWriteInFlight?: boolean;
  /** 生日解鎖 opened 智能 IDEA on an unconfirmed dob — see the drawer's own prop. */
  onBirthdayUnlock?: () => void;
  /**
   * The drawer has closed on a client, so whatever 補資料 sitting it held is
   * over. Closes the analytics batch — see `flushFactBatch` in ClientV2Bridge.
   */
  onFactsSessionEnd?: () => void;
  /**
   * The 招募 tools' own feature gates, resolved in the container. A false one
   * greys its card rather than withholding it, so the tool row is the same
   * shape for every adviser.
   */
  teamHubAvailable?: boolean;
  teamBotAvailable?: boolean;
  aiCoachAvailable?: boolean;
  /**
   * A.I. 智能助理 (FEATURE_GPT or Ultra). Without it the A.I. cards stay on screen,
   * greyed, and a tap offers the upgrade instead of opening /gpt-options — which
   * would send the adviser to /home.
   */
  aiAvailable?: boolean;
  /**
   * Whether 招募模式 is unlocked for this adviser (campaign code
   * FEATURE_RECRUITMODE). Gates the drawer's whole recruitment surface; the
   * 招募 TAB on this page is deliberately not gated by it — see
   * shouldEnableRecruitmentMode.
   */
  recruitmentMode?: boolean;
  onTrackingMet?: (client: QueueClient, met: number) => void;
  onTrackingOpportunity?: (client: QueueClient, opportunity: string | null) => void;
  onDrawerAction?: (action: ClientDrawerAction, client: QueueClient) => void;
  /**
   * Announces why a control the client's connection status withholds did
   * nothing (clientEntitlements.ts). Wired to a toast in ClientV2Bridge — the
   * page and its leaves stay free of react-toastify.
   */
  onRestricted?: (client: QueueClient, capability: ClientCapability) => void;
  onAddClient?: () => void;
  /**
   * A client created since this page last ran — the store remembers the id
   * because the add happens on ANOTHER ROUTE, which unmounts this page
   * (`handleAddClient` pushes /client_personal_form). The row is already in
   * `clientData` by the time this mounts, so the id-diff below cannot see it.
   */
  lastAddedClientId?: string | null;
  /** Called once the id above has been used, so it cannot flash twice. */
  onSpotlightSpent?: () => void;
  onPotentialClients?: () => void;
  onImportClients?: () => void;
  onAiSelect?: () => void;
  /** Opens the C.P.I. explainer behind the client-base indicator. */
  /**
   * Opens the C.P.I. explainer. It is handed the band the indicator is showing
   * rather than being left to re-derive it: `clientBaseLevel` below is sticky
   * across a refresh that drops the carrier, so a second, independent read
   * would resolve to 資料不足 while the indicator still reads 高.
   */
  onClientBase?: (clientBaseLevel: number | null) => void;
  // 回到舊版, 通知 and 說明 are not page props any more — ClientV2Bridge portals
  // all three straight into the top navigation bar, where V1 puts its own
  // 試用新版客戶頁/notification bell/help counterparts.
  canOpenFilter?: () => boolean;
  onManageFamilies?: () => void;
  potentialCount?: number;
  voiceControl?: React.ReactNode;
  voiceFilterRequest?: { token: number; searchTerm: string; filters: ClientFilters } | null;
  focusClientRequest?: { token: number; clientId: string } | null;
  legacyStateRequest?: ClientLegacyStateRequest | null;
  /**
   * "Show me this date on the calendar" — today from the meeting-note dialog's
   * calendar button. `day` is 'YYYY-MM-DD'; the token is what makes a second
   * request for the SAME day take effect, since the dialog can be opened from
   * this very page and pushing /clientv2 onto /clientv2 remounts nothing.
   */
  calendarRequest?: { token: number; day: string } | null;
  familyFilterRequest?: {
    token: number;
    oldFamilyName: string;
    newFamilyName?: string;
  } | null;
  onToggleClientToSend?: (client: QueueClient) => void;
  isMovePolicy?: boolean;
  /** Source client of the policy being moved — it cannot be its own target. */
  movePolicySourceId?: string;
  onSelectMovePolicyTarget?: (client: QueueClient) => void;
  /**
   * Leaves move-policy selection mode entirely (mirrors V1 cancelMovePolicy's
   * history.goBack exit). Rendered as the banner's cancel button.
   */
  onCancelMovePolicy?: () => void;
};

const ClientV2 = (props: ClientV2Props) => {
  const { clientData, policyDue, userEmail, currency, figure, families, usedTags, now } = props;
  const { getClient, fetchFigureAdviser, getUser, initPhaseEmail } = props;

  // The adviser notes (招募 stage, scores, chips, Smart IDEA marks) arrive on
  // each client's factsheet; hand them to the notes store before anything reads.
  useSeedClientNotes(clientData, userEmail);

  // Where the adviser was last time, restored exactly as V1 does
  // (clientPage.js:424-466). Read once, in state initialisers, so the deep-link
  // effects below — which run after mount — always win over the restored value.
  const restored = useRef<PersistedClientView | null>(null);
  if (restored.current === null) restored.current = readPersistedClientView();
  // V1: `location.select` replaces the stored clientState, and drops the stored
  // opening client unless the link carries its own `preSelectedClientIds`.
  const deepLinkSelect = typeof props.location?.select === 'string';
  const deepLinkKeepsSelection = Array.isArray(props.location?.preSelectedClientIds);

  const [selectedId, setSelectedId] = useState<string | null>(
    deepLinkSelect && !deepLinkKeepsSelection ? null : restored.current.clientId
  );
  const [tab, setTab] = useState<ClientTab>(restored.current.tab);
  // V1 opens on clientState '' — no client-side sort, so the list keeps the
  // server's newest-first order. The two sorts are opt-in from the menu.
  const [sort, setSort] = useState<ClientSort>(restored.current.sort);
  // List or calendar, and what the calendar counts. Both are remembered the way
  // the sort is — a preference about how to READ the list, not a narrowing of
  // which list. The calendar itself is wired up on the reminder tab below.
  const [view, setView] = useState<ClientListMode>(restored.current.view);
  const [calendarFilter, setCalendarFilter] = useState<CalendarFilter>(restored.current.calendarFilter);
  const [filters, setFilters] = useState(createDefaultClientFilters);
  // Which stat tile is narrowing the list, if any. Deliberately NOT persisted
  // with the view position below: it is a filter, and filters are left out of
  // the restore for the same reason V1 leaves its filterObject out.
  const [statFilter, setStatFilter] = useState<ClientStatKey | null>(null);
  /**
   * 招募模式 — the whole page as a recruitment workspace, as opposed to the 招募
   * TAB, which is a filter of the ordinary page. Gated on the campaign code
   * (props.recruitmentMode, FEATURE_RECRUITMODE); without it the 招募 chip keeps
   * doing exactly what it does today and nothing below ever renders.
   *
   * The remembered flag is re-checked against the gate on read, so an adviser
   * whose code lapses does not come back to a page they cannot leave.
   */
  const [recruitMode, setRecruitMode] = useState(() => readRecruitMode(!!props.recruitmentMode));
  const [recruitTab, setRecruitTab] = useState<RecruitTab>('all');
  const [recruitStat, setRecruitStat] = useState<RecruitStatKey | null>(null);
  const [recruitSort, setRecruitSort] = useState<RecruitSort>('default');
  // The initialiser above reads the gate once, but it is not settled at mount:
  // a pilot TEAM's code arrives with the functions fetch (`campaignCodes`
  // starts empty), so a hard reload would never restore the mode, and a code
  // that lapses mid-session would leave the page in it. Re-read on every
  // change of the gate — the flag itself is untouched, only whether it counts.
  useEffect(() => {
    setRecruitMode(readRecruitMode(!!props.recruitmentMode));
  }, [props.recruitmentMode]);
  /**
   * One-shot request for a drawer readout that the dispatcher cannot reach —
   * 招募 IDEA and 候選人評估 are mounted by the drawer, not navigated to. Both
   * a suggestion's CTA and a row's shortcut raise one.
   */
  const [factsRequest, setFactsRequest] = useState<{ token: number; clientId: string; focus: QuickFactKey } | null>(
    null
  );
  const [recruitSurfaceRequest, setRecruitSurfaceRequest] = useState<{
    token: number;
    clientId: string;
    surface: 'idea' | 'score' | 'quiz' | 'drawer';
  } | null>(null);
  /**
   * The mode transition. `wipe` is the overlay's origin while it is playing,
   * `tilesEntering` is the beat where the four tiles turn in behind it.
   */
  const [wipe, setWipe] = useState<{ origin: WipeOrigin; to: 'recruit' | 'business' } | null>(null);
  const [tilesEntering, setTilesEntering] = useState(false);
  /**
   * The row 階段 pill's menu. Owned here rather than per row: one menu for the
   * whole list, anchored to whichever pill opened it — two hundred rows each
   * carrying a closed MUI Menu is two hundred portals nobody opened.
   */
  const [stageMenu, setStageMenu] = useState<{ client: QueueClient; anchor: HTMLElement } | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const transitionTimers = useRef<number[]>([]);
  const [searchTerm, setSearchTerm] = useState(restored.current.searchTerm);
  const [page, setPage] = useState(restored.current.page);
  const [filterOpen, setFilterOpen] = useState(false);
  const [legacyState, setLegacyState] = useState<ClientLegacyStateRequest | null>(
    !deepLinkSelect && restored.current.legacyState
      ? {
          state: restored.current.legacyState,
          token: 0,
          ...(restored.current.preSelectedClientIds.length ? { clientIds: restored.current.preSelectedClientIds } : {}),
          // `lucky` narrows to one client, and that client is stored separately
          // (V1's selectedLuckyClientObj) rather than in the state name.
          ...(restored.current.legacyState === 'lucky' && restored.current.luckyClientId
            ? { clientId: restored.current.luckyClientId }
            : {})
        }
      : null
  );

  // `getClient` is the heaviest adviser query in the app (two collection scans,
  // five bulk finds and a whole-base CPI pass), and login/boot already runs it
  // once via loginFetch. V1 never refetches on entry either. So this only asks
  // for the list when it genuinely has none, and never while a fetch is already
  // in flight. Everything else stays current without it:
  // every mutation refreshes its own client through GET_CLIENT_BY_ID, and
  // `withClientScoreLevels` rebuilds the CPI bands that refresh cannot compute.
  // The command bar's refresh control covers the rest, on demand.
  //
  // Entering the page also drops the abandoned add-client / add-policy drafts,
  // as V1 does (clientPage.js:479-484), so the next form starts empty.
  //
  // `getUser` is V1's mount fetch too: the family palette, the tag palette and
  // the Pro subscription gate all read `user.userDetails`, and few other pages
  // refresh it — without this, a tag added from the drawer never reaches the
  // sidebar's tag cloud.
  //
  // On the way out, `initPhaseEmail` resets `user.approvePhase`, which V1 also
  // does. SendVerificationEmailDialog fires its accepted-branch on any update
  // while it is open, without needing a transition, so a stale `'accepted'`
  // would make the next verification dialog behave as if it had just succeeded.
  // (V1's other two unmount resets are deliberately not carried over:
  // `initPremiumViewPhase` clears policy state nothing in src/ reads, and
  // `endGuideline` cannot matter while ClientV2 starts no tours.)
  useEffect(() => {
    clearClientFormDrafts();
    // Drop any orphaned `clientSortBy` on the way in. It is V1's hand-off key,
    // consumed and deleted by store/client/api.js on the very next getClient —
    // so one still sitting here was set by a V1 entry point that never got its
    // fetch. Left alone it would narrow OUR next getClient instead, and that
    // response replaces `clientData` for the whole page. This page asks for the
    // 跟進 tab by name (requestClientV2Tab) and never wants a shortened list.
    clearLegacyClientSort();
    if (clientData.length === 0 && !props.fetchingClients) getClient?.();
    fetchFigureAdviser?.();
    getUser?.();
    return () => initPhaseEmail?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror of V1's setLocalStorage: the position is rewritten whenever it
  // changes, so any navigation away — including a hard one — can come back to
  // it. Filters are excluded, as they are in V1.
  useEffect(() => {
    writePersistedClientView({
      page,
      searchTerm,
      legacyState: legacyState?.state ?? null,
      clientId: selectedId,
      preSelectedClientIds: legacyState?.clientIds ?? [],
      luckyClientId: legacyState?.state === 'lucky' ? legacyState.clientId ?? null : null,
      tab,
      sort,
      view,
      calendarFilter
    });
  }, [page, searchTerm, legacyState, selectedId, tab, sort, view, calendarFilter]);

  useEffect(() => {
    const request = props.voiceFilterRequest;
    if (!request) return;
    setLegacyState(null);
    setSearchTerm(request.searchTerm);
    setFilters(request.filters);
    setPage(0);
    setFilterOpen(false);
  }, [props.voiceFilterRequest]);

  // A notification or deep link that names a client opens that client. V1
  // scrolled its expanded accordion entry into view (scrollToSelected) and, for
  // the GPT links, paged to the entry first (clientPage.js:155-158). ClientV2's
  // equivalent is to page to the client and open its drawer.
  const pendingFocusRef = useRef<string | null>(null);
  // One client-open per request, tracked by token.
  //
  // `clientData` has to stay in the deps: a deep link routinely arrives before
  // the list it names, so the request is held until the client turns up. But the
  // Bridge never clears `focusClientRequest` — it is set from three places
  // (a location deep link, A.I. 精選, and the notification centre) and then
  // stands for the life of the page — so with `clientData` alone deciding, every
  // later list refresh re-ran this and dragged the panel back to that client.
  // GET_CLIENT_BY_ID_SUCCESS rebuilds `clientData` as a new array (client/duck.js),
  // and a factsheet write chains exactly that: the adviser opened someone else,
  // answered one question about them, and the drawer jumped back to whoever the
  // notification had named. Marked consumed only once it is acted on, so a
  // request waiting for its client is still honoured when the client arrives.
  const consumedFocusRef = useRef<number | null>(null);
  useEffect(() => {
    const request = props.focusClientRequest;
    if (!request || request.token === consumedFocusRef.current) return;
    if (!clientData.some(client => client._id === request.clientId)) return;
    consumedFocusRef.current = request.token;
    setSelectedId(request.clientId);
    pendingFocusRef.current = request.clientId;
  }, [props.focusClientRequest, clientData]);

  useEffect(() => {
    const request = props.legacyStateRequest;
    if (!request) return;
    setLegacyState(request);
    setTab(request.state === 'allReminder' ? 'reminder' : 'all');
    setPage(0);
    setFilterOpen(false);
    // Only 'lucky' clears the search box, because only V1's aiSuggestClient
    // does (clientPage.js:224-229) — every other state leaves it alone, which
    // is the Slice 19 rule.
    if (request.state === 'lucky') setSearchTerm('');
  }, [props.legacyStateRequest]);

  // Two list states depend on data the ADVISER DASHBOARD normally fetches:
  // coverage gaps read `policiesData` (connectedUserPolicies) and
  // dateBackBirthdayCount reads `PastBirthdayOfUsers` (getAdviserDashboard).
  // Arriving here directly — a deep link, or a reload on /clientv2 — leaves them
  // empty, and the filters would then silently show the wrong list. Fetch each
  // once, on demand. Deliberately not part of the mount dispatch: they are extra
  // API round-trips that ordinary page loads have no use for.
  const requestedPolicyGapRef = useRef(false);
  useEffect(() => {
    if (!legacyState || !(POLICY_GAP_STATES as readonly string[]).includes(legacyState.state)) return;
    if (props.policiesData || requestedPolicyGapRef.current) return;
    requestedPolicyGapRef.current = true;
    props.connectedUserPolicies?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyState, props.policiesData]);

  const requestedPastBirthdayRef = useRef(false);
  useEffect(() => {
    if (legacyState?.state !== 'dateBackBirthdayCount') return;
    if (props.pastBirthdayUsers || requestedPastBirthdayRef.current) return;
    requestedPastBirthdayRef.current = true;
    props.getAdviserDashboard?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyState, props.pastBirthdayUsers]);

  useEffect(() => {
    const request = props.familyFilterRequest;
    if (!request) return;
    setFilters(current => migrateFamilyFilter(current, request.oldFamilyName, request.newFamilyName));
    setPage(0);
  }, [props.familyFilterRequest]);

  // A tag deleted from the adviser's palette must leave the active filter with
  // it, or the list silently filters on something that no longer exists — V1
  // does this in componentDidUpdate (updateStateFilterTags, clientPage.js:604).
  // Skipped on mount for the same reason V1 only reacts to a *change*: the
  // palette is empty until getUser resolves.
  const knownTagsRef = useRef<ClientTag[] | undefined>(undefined);
  useEffect(() => {
    const previous = knownTagsRef.current;
    knownTagsRef.current = usedTags;
    if (previous === undefined || !usedTags) return;
    const known = new Set(usedTags.map(tag => tag.text));
    setFilters(current => {
      const kept = current.tags.filter(tag => known.has(tag));
      return kept.length === current.tags.length ? current : { ...current, tags: kept };
    });
  }, [usedTags]);

  // Captures the element that had focus when a selection was made (a row or
  // queue-item button, whether reached by mouse or keyboard - both leave the
  // activated <button> as document.activeElement). ClientRow is not a
  // forwardRef and its onSelect/onSelectClient only pass the client id, so
  // this is the only way to recover "which button opened the drawer" and
  // hand it back to ClientDetailDrawer's onClose.
  const triggerRef = useRef<HTMLElement | null>(null);

  // One instant for the whole page: buildTodayQueue and ClientDetailDrawer both
  // answer "is this today", and they must agree — so it is captured once rather
  // than drifting to a new instant on every unrelated re-render. A caller that
  // supplies `now` pins it outright (the tests do).
  //
  // But captured once is not captured FOREVER, which is what this was. Every
  // reader below asks a question about today — 重點跟進, the stat tiles, the
  // filters, and `filterClientsByLegacyState`, which is V2's rebuild of V1's own
  // 今明生日 and 提醒 lists — and this page ships in a Capacitor app, which is
  // backgrounded rather than closed. The mount routinely outlives the day it
  // started in, and V1 has never had the problem to compare against: clientPage
  // calls `moment()` inline at render, so after midnight the two pages answered
  // differently about the same client, on the very lists built to match.
  //
  // The tick is the calendar DAY, not the minute. Every consumer floors to
  // startOf('day'), so a fresh instant each minute would recompute the queue,
  // the stats and the whole filter chain 1439 times a day for no change in
  // answer; returning `previous` unchanged lets React bail out instead. The
  // visibility listener is the half that matters on a phone — a backgrounded
  // timer is throttled or suspended, so the resume is when the day has to be
  // re-read.
  const [liveNow, setLiveNow] = useState(() => moment());

  useEffect(() => {
    if (now) return undefined;
    const rereadIfDayTurned = () => setLiveNow(previous => (previous.isSame(moment(), 'day') ? previous : moment()));
    const timer = window.setInterval(rereadIfDayTurned, DAY_ROLLOVER_CHECK_MS);
    document.addEventListener('visibilitychange', rereadIfDayTurned);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', rereadIfDayTurned);
    };
  }, [now]);

  // No `useMemo`: both branches are already stable references (a prop and a
  // piece of state), so memoising a bare `||` of its own two dependencies can
  // only return what the expression returns. The memo here used to earn its
  // keep by being where `moment()` was CALLED; `liveNow` took that over.
  const effectiveNow = now || liveNow;

  const queue = useMemo(
    () => buildTodayQueue({ clients: clientData, adviserEmail: userEmail, policyDue, now: effectiveNow }),
    [clientData, userEmail, policyDue, effectiveNow]
  );

  // `effectiveNow` is a MomentInput — the `now` prop is typed that way and the
  // tests pass a string. buildTodayQueue and clientStatsOf each re-parse it
  // internally; the calendar needs a real Moment to clone and format, so it is
  // parsed once here rather than at four call sites.
  const calendarNow = useMemo(() => moment(effectiveNow), [effectiveNow]);

  // The month on screen and the day picked in it. Declared here rather than
  // beside `view` above because both are seeded from the clock, which is only
  // settled on the lines above. Deliberately NOT persisted: coming back to the
  // page should show the current month, not the one left behind months ago.
  const [visibleMonth, setVisibleMonth] = useState<moment.Moment>(() => moment(effectiveNow).startOf('month'));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // The calendar is the REMINDER TAB's view, and the toggle that leaves it is
  // rendered only there. Deriving this from the tab (rather than storing an
  // "is the calendar open" flag) means a tab change cannot strand the adviser
  // in a mode whose only control has just unmounted.
  const calendarOpen = tab === 'reminder' && view === 'calendar';

  // The filter panel is switched off in the calendar; one left open on a phone
  // would be a dead overlay over the grid.
  useEffect(() => {
    if (calendarOpen) setFilterOpen(false);
  }, [calendarOpen]);

  // Sweeps the whole client array, like the stat tiles and the today-queue
  // above it — and like them it must never be handed a paged slice.
  const calendarMonth = useMemo(
    () => reminderCalendarMonth(clientData, userEmail, visibleMonth, calendarNow, calendarFilter),
    [clientData, userEmail, visibleMonth, calendarNow, calendarFilter]
  );

  const handleCalendarMonth = (move: 'prev' | 'next' | 'today'): void => {
    // A day number means nothing once the month changes underneath it.
    setSelectedDay(null);
    if (move === 'today') setVisibleMonth(calendarNow.clone().startOf('month'));
    else
      setVisibleMonth(month =>
        month
          .clone()
          .add(move === 'next' ? 1 : -1, 'month')
          .startOf('month')
      );
  };

  // The meeting-note dialog's calendar button. Lands on the reminder tab in
  // calendar view, paged to the reminder's OWN month with its day selected —
  // landing on the current month would show an empty grid for a reminder set
  // three months out, and leave the adviser to find it.
  //
  // Keyed on the token, not the day: the dialog can be opened from this page,
  // so the same day can be asked for twice in a row and the second ask must
  // still take effect.
  useEffect(() => {
    const request = props.calendarRequest;
    if (!request) return;
    const day = moment(request.day, 'YYYY-MM-DD', true);
    // A malformed date would page the grid to an Invalid Date month, which
    // renders as an empty calendar with no way back except 本月.
    if (!day.isValid()) return;
    // The calendar is business-only: 招募模式's tab row has no 📅 toggle, so
    // opening it under the mode left the month grid in place of the candidate
    // list with no way back but 業務. The reminder was asked for by name, so
    // the mode steps aside for it (no wipe — this is navigation, not a switch).
    if (recruitMode) {
      setRecruitStat(null);
      setRecruitSort('default');
      setRecruitMode(false);
      writeRecruitMode(false);
    }
    setTab('reminder');
    setView('calendar');
    // The calendar's own filter is persisted across visits (clientViewState), so
    // an adviser who last looked at 生日 would land on the right day with the
    // reminder filtered out of it. The request is always about a reminder, so
    // widen to 全部: the reminder is shown, and so is anything else that day.
    setCalendarFilter('all');
    setVisibleMonth(day.clone().startOf('month'));
    setSelectedDay(day.format('YYYY-MM-DD'));
    setPage(0);
    // The dialog is usually opened from the client drawer, which is still open
    // underneath it. Below 901px that drawer covers the page, so the calendar
    // opened behind it and the button looked like it had done nothing. Close
    // it there. At 901px and up the drawer sits beside the page (the list
    // shrinks for it), so it stays: the client and their month, side by side.
    const sideBySide = typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 901px)').matches;
    if (!sideBySide) setSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.calendarRequest?.token]);

  // Stat tiles + tab counts read the FULL client array (handoff PART 6c) - only
  // the list render below is ever filtered/sorted. Memoised on the same inputs
  // so an unrelated re-render (opening the drawer) doesn't recompute them.
  const adviserPersonality = props.userDetails?.personality ?? null;

  // 補資料 on 重點跟進 — one client one field away from a C.P.I. band, for the
  // slot the dated signals leave free (factsFillIn.ts).
  const [fillInSnoozes, setFillInSnoozes] = useState<Record<string, string>>(() => readFillInSnoozes());
  const factsFillIn = useMemo(
    () =>
      queue.length >= QUEUE_PREVIEW_SIZE
        ? null
        : pickFactsFillIn({
            clients: clientData,
            adviserEmail: userEmail,
            adviserPersonality,
            excludeIds: new Set(queue.slice(0, QUEUE_PREVIEW_SIZE).map(entry => entry.client._id)),
            snoozed: fillInSnoozes,
            now: effectiveNow
          }),
    [queue, clientData, userEmail, adviserPersonality, fillInSnoozes, effectiveNow]
  );
  const handleFillIn = (fill: FactsFillIn) => {
    // Not handleSelectClient: it toggles, and would close the drawer already
    // open on this client instead of opening the card in it.
    if (fill.client._id !== selectedId) handleSelectClient(fill.client._id);
    setFactsRequest({ token: Date.now(), clientId: fill.client._id, focus: fill.field });
  };
  const handleSnoozeFillIn = (fill: FactsFillIn) => setFillInSnoozes(snoozeFillIn(fill.client._id, effectiveNow));

  const stats = useMemo(
    () => clientStatsOf(clientData, { policyDue, now: effectiveNow, adviserEmail: userEmail }),
    [clientData, policyDue, effectiveNow, userEmail]
  );

  const tabCounts = useMemo(() => tabCountsOf(clientData, userEmail), [clientData, userEmail]);

  /**
   * 招募模式's own full-array reads, the same shape as the two above. The base
   * is the 招募 tab's own predicate — the mode is that list, worked rather than
   * browsed — and every count below is over the WHOLE candidate array, not the
   * tab or tile narrowing of it.
   */
  const recruitBase = useMemo(() => filterClientsByTab(clientData, 'recruit', userEmail), [clientData, userEmail]);
  // The candidates, not the whole book, and only while the mode is ON: the
  // store is keyed by client id and has nothing to say about the other few
  // hundred, and the tiles, tab counts and 今日招募 below are only rendered in
  // the mode — reading it for a business-mode refresh is work nobody sees.
  const recruitFacts = useRecruitFacts(recruitBase, recruitMode);
  const recruitStats = useMemo(
    () => recruitStatsOf(recruitBase, recruitFacts, { now: effectiveNow, adviserEmail: userEmail }),
    [recruitBase, recruitFacts, effectiveNow, userEmail]
  );
  const recruitCounts = useMemo(() => recruitTabCountsOf(recruitBase, recruitFacts), [recruitBase, recruitFacts]);
  const recruitSuggestions = useMemo(
    () =>
      recruitMode
        ? buildRecruitSuggestions({
            candidates: recruitBase,
            facts: recruitFacts,
            now: effectiveNow,
            adviserEmail: userEmail
          })
        : [],
    [recruitMode, recruitBase, recruitFacts, effectiveNow, userEmail]
  );

  // Adviser-level, and therefore another full-array read (PART 6c). It is also
  // sticky for the session: the server piggybacks the band on one client, and a
  // GET_CLIENT_BY_ID refresh of that client returns a payload without it — the
  // indicator must not go grey just because the carrier was refreshed.
  const lastClientBaseRef = useRef<number | null>(null);
  const clientBaseLevel = useMemo(() => {
    const level = clientBaseLevelOf(clientData);
    if (level !== null) lastClientBaseRef.current = level;
    return level !== null ? level : lastClientBaseRef.current;
  }, [clientData]);

  // Sidebar choices also read the FULL array. The adviser-level family/tag
  // palettes are the source of truth; client factsheets fill any values that
  // are present in data but absent from those palettes.
  const filterOptions = useMemo(
    () => clientFilterOptionsOf(clientData, { adviserEmail: userEmail, families, usedTags }),
    [clientData, userEmail, families, usedTags]
  );

  // The visible list alone receives tab + sidebar + Smart Search filters,
  // then sorting. Stats, queue, tab counts and sidebar choices above remain
  // based on the complete clientData array regardless of these controls.
  const visibleClients = useMemo(() => {
    // 招募模式 narrows candidates -> tab -> tile, then hands the result to the
    // SAME search, sidebar and sort the business list uses. The controls below
    // the tab row do not change in this mode, so neither should the path they
    // take; only the two steps above them are the mode's own.
    if (recruitMode) {
      const narrowed = filterRecruitsByStat(
        filterRecruitsByTab(recruitBase, recruitTab, recruitFacts),
        recruitStat,
        recruitFacts,
        { now: effectiveNow, adviserEmail: userEmail }
      );
      const filtered = filterClientsBySearch(
        filterClients(narrowed, filters, { adviserEmail: userEmail, now: effectiveNow }),
        searchTerm,
        userEmail
      );
      const recruitVisible = sortAndFilterRecruitsBySort(filtered, recruitSort, recruitFacts);
      // A list state can still arrive while the mode is on (a notification or
      // deep link raises legacyStateRequest, which has no mode guard), and the
      // banner names it either way — so the list has to honour it, or the
      // banner is describing a narrowing that was never applied.
      return filterClientsByLegacyState(recruitVisible, legacyState, {
        adviserEmail: userEmail,
        now: effectiveNow,
        policiesData: props.policiesData,
        pastBirthdayUsers: props.pastBirthdayUsers
      });
    }
    const normallyVisible = sortClients(
      filterClientsBySearch(
        filterClients(
          filterClientsByStat(filterClientsByTab(clientData, tab, userEmail), statFilter, {
            policyDue,
            now: effectiveNow,
            adviserEmail: userEmail
          }),
          filters,
          {
            adviserEmail: userEmail,
            now: effectiveNow
          }
        ),
        searchTerm,
        userEmail
      ),
      sort,
      userEmail,
      tab
    );
    return filterClientsByLegacyState(normallyVisible, legacyState, {
      adviserEmail: userEmail,
      now: effectiveNow,
      policiesData: props.policiesData,
      pastBirthdayUsers: props.pastBirthdayUsers
    });
  }, [
    clientData,
    tab,
    statFilter,
    policyDue,
    filters,
    searchTerm,
    sort,
    userEmail,
    effectiveNow,
    legacyState,
    props.policiesData,
    props.pastBirthdayUsers,
    recruitMode,
    recruitBase,
    recruitFacts,
    recruitTab,
    recruitStat,
    recruitSort
  ]);

  // Paging to a focused client runs here, after the list has settled — a
  // legacy-state request commits in the same render, so `visibleClients` is
  // already the narrowed list. One attempt only: if the client is filtered out
  // there is no page to go to, and a lingering request must never hijack a
  // later, unrelated list change.
  useEffect(() => {
    const clientId = pendingFocusRef.current;
    if (!clientId) return;
    pendingFocusRef.current = null;
    const index = visibleClients.findIndex(client => client._id === clientId);
    if (index >= 0) setPage(Math.floor(index / CLIENT_PAGE_SIZE));
    // The request itself is a dependency because a focus that does not change
    // the list (no accompanying state) would otherwise never re-run this.
  }, [visibleClients, props.focusClientRequest]);

  const paginated = useMemo(() => paginateItems(visibleClients, page), [visibleClients, page]);

  // Data refreshes can shrink the current result set while the user is on a
  // later page. Keep the controlled page state aligned with the safe page the
  // paginator/list already render.
  useEffect(() => {
    const safePage = clampPage(page, visibleClients.length);
    if (safePage !== page) setPage(safePage);
  }, [page, visibleClients.length]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFilterOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [filterOpen]);

  const scoresFor = (client: QueueClient): AnalysisScores =>
    // getClientAnalysisScores (utils/client.js) is plain, untyped JS, so TS
    // sees its return value as `any`. The cast is safe, not a shortcut: it
    // asserts exactly the shape client-insights.ts documents that function
    // returning, which is the contract every consumer here (CoverageMeter,
    // ClientRow, ClientDetailDrawer) already relies on.
    getClientAnalysisScores(
      { ...client, factsheetId: qualifyingFactsheetOf(client, userEmail) },
      currency,
      figure
    ) as AnalysisScores;

  const handleCloseDrawer = () => {
    setSelectedId(null);
    // A surface request the drawer never honoured (not on 招募 view yet, or
    // the candidate's flag changed) must not outlive the drawer it was for, or
    // it opens a dialog by itself the next time that candidate is opened.
    setRecruitSurfaceRequest(null);
    setFactsRequest(null);
  };

  /**
   * A 補資料 sitting ends when the drawer does.
   *
   * The batch in ClientV2Bridge only closed on two events: answering a fact for
   * a DIFFERENT client, and leaving the page. Neither fires when the adviser
   * shuts the drawer and opens the same client again a minute later, so those
   * two sittings merged into one event — the funnel counted one visit where
   * there were two, and a failure from the second was attributed to a batch
   * opened by the first.
   *
   * Written as a cleanup rather than an `if (selectedId === null)` effect so it
   * fires on every kind of ending there is: the ✕, a tap outside, the client
   * being filtered out from under the drawer (the effect below), and unmount —
   * all of which are `setSelectedId` calls in different places that would each
   * have needed remembering.
   *
   * `onFactsSessionEnd` is deliberately out of the deps: it is rebuilt on every
   * render of the bridge, so including it would re-run this effect — and its
   * cleanup — continuously, flushing a batch mid-sitting. It only ever reads a
   * ref, so the identity captured here behaves identically to the current one.
   */
  useEffect(() => {
    if (selectedId === null) return undefined;
    return () => props.onFactsSessionEnd?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (selectedId !== null) return;
    const trigger = triggerRef.current;
    // Guard against the trigger having been removed from the document (e.g.
    // the client list re-rendered while the drawer was open) - focusing a
    // detached node is a silent no-op in most browsers but not guaranteed.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
    triggerRef.current = null;
  }, [selectedId]);

  // Move-policy mode deliberately does NOT intercept this. V1 selects a target
  // from the row's own checkbox and leaves the row (and everything else on the
  // page) behaving normally — intercepting every row click also swallowed
  // TodayQueue's, since it shares this handler.
  const handleSelectClient = (clientId: string) => {
    if (clientId === selectedId) {
      handleCloseDrawer();
      return;
    }
    triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setFilterOpen(false);
    setSelectedId(clientId);
  };

  // Which controls drop a legacy list state is NOT a free choice — it mirrors
  // whether the V1 control wrote `clientState` (clientPage.js):
  //   tab   -> `hanldlefilter(key)` sets clientState  => replaces it
  //   sort   -> V1's sort options ARE clientState values ('hightolow',
  //            'Nameatoz', ...) selected from the same <select>  => replaces it
  //   search -> `searchUpdated` touches only searchTerm/selectedId/page => KEEPS
  //   filter -> `applySearchFilter` touches only filterObject          => KEEPS
  //   reset  -> `resetSearch()` explicitly clears clientState + searchTerm
  //            + filterObject together                                 => clears
  // Keeping it on search/filter is what lets an adviser arriving from
  // AdviserBlast search inside the ClientToSend list without the msgOpt
  // checkboxes vanishing.
  /**
   * Entering and leaving 招募模式. The underlying ClientTab is moved to 招募 on
   * the way in and back to 全部 on the way out, so the page beneath the mode is
   * always describing the same set of people the mode is — and so V1, which
   * reads the tab back through `clientState`, sees a tab it understands either
   * way. The mode flag itself is stored separately (clientViewState) precisely
   * because V1 reads that key.
   */
  const applyEnterRecruitMode = () => {
    setLegacyState(null);
    setStatFilter(null);
    setRecruitStat(null);
    setRecruitSort('default');
    setRecruitTab('all');
    setView('list');
    setPage(0);
    setTab('recruit');
    setRecruitMode(true);
    writeRecruitMode(true);
  };

  const enterRecruitMode = () => playModeTransition('recruit', applyEnterRecruitMode);

  const applyExitRecruitMode = () => {
    setRecruitStat(null);
    setRecruitSort('default');
    setPage(0);
    setTab('all');
    setRecruitMode(false);
    writeRecruitMode(false);
  };

  const exitRecruitMode = () => playModeTransition('business', applyExitRecruitMode);

  /**
   * Plays the wipe, applies the mode at the point it covers the page, and lets
   * the tiles turn in behind it. Anyone with 「reduce motion」 on gets the same
   * state change with none of the above.
   *
   * `apply` is passed rather than called here so the two handlers stay the
   * plain state changes they were — this function owns the timing, not the mode.
   */
  const playModeTransition = (to: 'recruit' | 'business', apply: () => void) => {
    transitionTimers.current.forEach(id => window.clearTimeout(id));
    transitionTimers.current = [];

    if (prefersReducedMotion()) {
      apply();
      return;
    }

    pulse();
    const origin = wipeOriginOf(pageRef.current);
    if (!origin) {
      apply();
      return;
    }
    setWipe({ origin, to });
    transitionTimers.current.push(
      window.setTimeout(() => {
        apply();
        setTilesEntering(true);
      }, RECRUIT_APPLY_AT_MS),
      window.setTimeout(() => {
        setWipe(null);
        setTilesEntering(false);
      }, RECRUIT_TRANSITION_MS)
    );
  };

  useEffect(
    () => () => {
      transitionTimers.current.forEach(id => window.clearTimeout(id));
    },
    []
  );

  const openRecruitSurface = (client: QueueClient, surface: 'idea' | 'score' | 'quiz' | 'drawer') => {
    // The drawer remembers which view it opened a client in; setting it first
    // means the panel is already on 招募 when the request lands, rather than
    // flashing the business panel and switching.
    rememberRecruitView(client._id, true);
    // Not handleSelectClient: that one TOGGLES, so asking for 招募 IDEA on the
    // candidate whose drawer is already open used to close the drawer — and
    // leave the request behind to fire the next time they were opened.
    if (client._id !== selectedId) handleSelectClient(client._id);
    setRecruitSurfaceRequest({ token: Date.now(), clientId: client._id, surface });
  };

  const handleSuggestion = (suggestion: RecruitSuggestion) => {
    const { target, client } = suggestion;
    if (target.kind === 'browse') {
      // The two book-level suggestions are both 「go and look at the book」, and
      // the book is this page with the mode off.
      exitRecruitMode();
      return;
    }
    if (!client) return;
    if (target.kind === 'action') props.onDrawerAction?.(target.action, client);
    else openRecruitSurface(client, target.surface);
  };

  const handleRecruitStage = (client: QueueClient, anchor: HTMLElement) => setStageMenu({ client, anchor });

  const handleRecruitTab = (nextTab: RecruitTab) => {
    setRecruitTab(nextTab);
    setPage(0);
  };

  // A tile narrows INSIDE the current tab and is its own on/off control, the
  // same contract the business tiles have.
  const handleRecruitStat = (stat: RecruitStatKey) => {
    setRecruitStat(current => (current === stat ? null : stat));
    setPage(0);
  };

  const handleRecruitSort = (nextSort: RecruitSort) => {
    setRecruitSort(nextSort);
    setPage(0);
  };

  const handleTab = (nextTab: ClientTab) => {
    // The 招募 chip is the way in, but only for an adviser who has the code.
    // Without it the chip stays exactly what it has always been: a filter.
    if (nextTab === 'recruit' && props.recruitmentMode) {
      enterRecruitMode();
      return;
    }
    setLegacyState(null);
    setTab(nextTab);
    // A chip always means that tab's LIST. The calendar has one way in, the 📅
    // button, so tapping 提醒 can never land on a grid the adviser did not ask
    // for just because they left it open earlier.
    setView('list');
    setPage(0);
  };

  // The 📅 button: from any tab, into the reminder calendar; from the calendar,
  // back to the reminder list.
  const handleCalendar = () => {
    if (calendarOpen) {
      setView('list');
      return;
    }
    setLegacyState(null);
    setTab('reminder');
    setView('calendar');
    setPage(0);
  };

  const handleSort = (nextSort: ClientSort) => {
    setLegacyState(null);
    setSort(nextSort);
    setPage(0);
  };

  const handleFilters = (nextFilters: ReturnType<typeof createDefaultClientFilters>) => {
    setFilters(nextFilters);
    setPage(0);
  };

  // V1's family label is a filter shortcut, not an editor: it replaces only
  // the family portion of filterObject and toggles off when the same complete
  // set is clicked again (clientPage.js:189-212).
  const handleRowFamilyFilter = (clientFamilies: string[]) => {
    setFilters(current => {
      const sameFamilies =
        current.families.length === clientFamilies.length &&
        clientFamilies.every(family => current.families.includes(family));
      return { ...current, families: sameFamilies ? [] : [...clientFamilies] };
    });
    setPage(0);
  };

  // A stat tile narrows the list to the clients behind its figure, and the same
  // tile clears it again. It drops any legacy list state for the reason the tab
  // row does: a legacy state REPLACES the whole list, so the two cannot both
  // describe what is on screen. The tab, sidebar and search box are left alone —
  // like every other filter here, the tile composes with them.
  const handleStat = (stat: ClientStatKey) => {
    setLegacyState(null);
    setPage(0);
    // The calendar plots every client with a date and a tile narrows nothing
    // there, so a tap from the calendar leaves it for the list that tile is
    // about. 全部, not 提醒: the calendar parks the chips on 提醒, and 本月生日
    // inside the reminder list would show only the few with both. From the
    // calendar the tile is always switched ON, never toggled off by a tap that
    // is really asking to see it.
    if (calendarOpen) {
      setTab('all');
      setView('list');
      setStatFilter(stat);
      return;
    }
    setStatFilter(current => (current === stat ? null : stat));
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term.replace(/vvip/gi, 'v.vip'));
    setPage(0);
  };

  // Picking a list state from the menu, the V1 <select> equivalent. Local
  // selection needs no token — the token only exists to let the bridge replay
  // the same deep link twice.
  const localStateTokenRef = useRef(0);
  const handleLegacyStateSelect = (state: ClientLegacyState | null) => {
    setPage(0);
    if (!state) {
      setLegacyState(null);
      return;
    }
    localStateTokenRef.current -= 1;
    setLegacyState({ state, token: localStateTokenRef.current });
    setTab(state === 'allReminder' ? 'reminder' : 'all');
  };

  const handleResetFilters = () => {
    setLegacyState(null);
    setFilters(createDefaultClientFilters());
    setStatFilter(null);
    setRecruitStat(null);
    setRecruitSort('default');
    setSearchTerm('');
    setPage(0);
  };

  // The sidebar reset intentionally preserves the current tab (V1 parity),
  // but the selected-list banner promises the FULL client list. Its CTA
  // therefore clears every narrowing control, including the tab itself.
  const handleShowAllClients = () => {
    handleResetFilters();
    // In 招募模式 the full list is every candidate, so the reset lands on this
    // mode's own 全部 and leaves the underlying tab alone. setTab('all') here
    // would also rewrite `clientState`, which Client V1 reads back — the page
    // beneath the mode has to keep saying 'recruitment' while the mode is on.
    if (recruitMode) setRecruitTab('all');
    else setTab('all');
    setFilterOpen(false);
  };

  const handleFilterToggle = () => {
    if (!filterOpen && props.canOpenFilter && !props.canOpenFilter()) return;
    setFilterOpen(open => !open);
  };

  const selected = clientData.find(client => client._id === selectedId) || null;
  // The drawer's 建議下一步 line reads the queue the 重點跟進 list is already
  // built from, rather than re-deriving a signal of its own — the two can then
  // never tell the adviser different things about the same client on the same
  // screen. `queue` holds only clients that matched a signal, so the lookup
  // returning nothing IS "there is nothing to suggest".
  const selectedEntry = selected ? queue.find(entry => entry.client._id === selected._id) : undefined;

  // FLIP reorder: when the visible rows change order (sort/tab/filter/search),
  // each surviving row slides from its old position to the new one instead of
  // teleporting. Rows that just mounted are left to the CSS entrance stagger;
  // rows that leave simply vanish (a real exit animation would need
  // AnimatePresence-style bookkeeping - deferred with the framer-motion call).
  // Positions are read in useLayoutEffect - after React commits, before paint -
  // so the inversion transform lands before the user ever sees the new order.
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowTops = useRef(new Map<string, number>());
  const rowTimers = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const previous = rowTops.current;
    const next = new Map<string, number>();
    list.querySelectorAll<HTMLElement>('[data-client-id]').forEach(row => {
      const id = row.dataset.clientId as string;
      const top = row.getBoundingClientRect().top;
      next.set(id, top);
      const before = previous.get(id);
      if (before === undefined || before === top || reduced) return;
      const staleTimer = rowTimers.current.get(id);
      if (staleTimer !== undefined) window.clearTimeout(staleTimer);
      row.style.transition = 'none';
      row.style.transform = `translateY(${before - top}px)`;
      // Force layout so the inverted position commits before the transition
      // back to identity is set - this is the "Play" in FLIP.
      row.getBoundingClientRect();
      row.style.transition = 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
      row.style.transform = '';
      // The inline transition must not outlive the move (it would slow the
      // row's hover lift); self-cleaning timer, re-armed if a FLIP interrupts.
      rowTimers.current.set(
        id,
        window.setTimeout(() => {
          row.style.transition = '';
          rowTimers.current.delete(id);
        }, 320)
      );
    });
    rowTops.current = next;
  });

  useEffect(() => {
    const timers = rowTimers.current;
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, []);

  // New-client spotlight: an add/import comes back as a `clientData` carrying
  // an id the page has not seen before. The ref only starts diffing from the
  // first NON-EMPTY list, so the page-load population itself is never mistaken
  // for an add — and an emptied list (account switch) re-primes instead of
  // flashing the whole refilled book on the next fetch. A bulk import spots
  // many new ids; the first in server order takes the one flash.
  const knownClientIdsRef = useRef<Set<string> | null>(null);
  const [spotlightId, setSpotlightId] = useState<string | null>(null);

  useEffect(() => {
    const ids = new Set(clientData.map(client => client._id));
    const known = knownClientIdsRef.current;
    knownClientIdsRef.current = ids;
    if (known === null || known.size === 0 || ids.size === 0) return;
    const added = clientData.filter(client => !known.has(client._id));
    if (added.length > 0) setSpotlightId(added[0]._id);
  }, [clientData]);

  // The spotlight only scrolls/flashes when the new client's row is actually
  // rendered — if the current tab/filters hide it, or it sits on another page,
  // the highlight quietly stands down rather than dragging the list somewhere
  // the adviser did not ask to go.
  useEffect(() => {
    if (!spotlightId) return undefined;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-client-id="${spotlightId}"]`);
    if (!row) {
      setSpotlightId(null);
      return undefined;
    }
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // jsdom has no scrollIntoView, so the call is optional; every real engine
    // (and the Capacitor webviews) takes the smooth scroll.
    row.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setSpotlightId(null), SPOTLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [spotlightId]);

  // The add that happened on ANOTHER ROUTE. 新增客戶 pushes /client_personal_form,
  // which unmounts this page; the create answers with GET_CLIENT_BY_ID, and its
  // reducer inserts the row into `clientData` while the adviser is still on the
  // form. So by the time this page mounts the newcomer is already in the list,
  // the id-diff above reads it as part of the baseline, and the row an adviser
  // most needs pointing at is the one row that never got pointed at.
  //
  // The store remembers the id instead (`lastAddedClientId`), which is the only
  // signal that survives the unmount. Spent on arrival — a flag that outlived
  // its flash would fire again on the next tab change or remount, pointing at a
  // client who stopped being new some time ago.
  useEffect(() => {
    if (!props.lastAddedClientId) return;
    setSpotlightId(props.lastAddedClientId);
    props.onSpotlightSpent?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.lastAddedClientId]);

  // Below 901px the drawer covers the FAB's corner (same breakpoint the
  // sidebar/list-shrink rules key off, in styles.scss), so the FAB has to
  // unmount there while the drawer is open. At and above it the CSS instead
  // slides the FAB in front of the drawer (see `.clientv2-add-fab` in
  // styles.scss), so it must stay mounted, focusable and announced.
  const [isWideViewport, setIsWideViewport] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 901px)').matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(min-width: 901px)');
    const onChange = () => setIsWideViewport(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // A selected client that is no longer in the list has been deleted (the
  // drawer's 刪除客戶 dispatches DELETE_CLIENT, whose epic follows with a full
  // GET_CLIENT) or removed by someone else's refresh. Drop the selection so the
  // drawer closes and the remembered id cannot reopen a record that is gone.
  //
  // Guarded on a NON-EMPTY list: `clientData` is empty while the mount fetch is
  // in flight, and clearing there would throw away the position restored from
  // localStorage before the list ever arrives.
  useEffect(() => {
    if (selectedId === null || clientData.length === 0) return;
    if (clientData.some(client => client._id === selectedId)) return;
    setSelectedId(null);
    clearPersistedClient();
  }, [clientData, selectedId]);

  // A list filtered down to nothing has to say so. Without this the page draws a
  // blank gap between the tab row and the FAB, which reads as a failed load
  // rather than a filter that matched nobody — and the clickable stat tiles put
  // that one click away, since a tile reading 「0 名」 is itself a valid filter.
  //
  // Saying it is ONE element's job, not two. When something is narrowing the
  // list the banner below already names what is applied and carries the way
  // out, so it takes the empty message too (`is-empty`) and the dashed panel
  // stands down — otherwise the page stacked two boxes repeating the same
  // sentence over two buttons for the same job. The panel is left with the
  // case the banner cannot cover: nothing narrowing the list at all (an empty
  // 跟進 tab, or an adviser with no clients), where there is correctly no
  // button, since neither reset would change what is on screen.
  const canResetList =
    hasActiveClientFilters(filters) ||
    statFilter !== null ||
    // The recruitment tiles narrow the list exactly as the business ones do,
    // so without this a tapped tile left a shortened list with no banner
    // saying why and no 清除 to undo it.
    recruitStat !== null ||
    (recruitMode && (recruitSort === 'quiz' || recruitSort === 'unrated')) ||
    searchTerm.trim().length > 0 ||
    legacyState !== null;
  // Only reached when nothing is narrowing the list (the banner owns the
  // narrowed-and-empty case, and always words it as a filter result). So the
  // only question left here is whether the adviser has any clients at all.
  const emptyListMessageId = clientData.length === 0 ? 'clientv2-list-empty-none' : 'clientv2-list-empty';
  // One flag for both, so the banner and the list can never disagree about
  // whether there is anything to show.
  const listIsEmpty = paginated.items.length === 0;

  // V1 prepends a mail button to each row in these states (clientPage.js:2543,
  // 2559) — birthday states open the greeting dialog, reminder states the
  // meeting note. Both flows already exist as `onBirthday` and the
  // `meeting-note` drawer action, so the row reuses them rather than adding a
  // second path to the same dialogs.
  // The v46 提醒 tab is the same list as V1's allReminder state (identical
  // predicate, identical date order), so it carries the same per-row control —
  // reaching the reminder list by tab rather than by menu must not silently
  // drop it.
  const rowMailAction: 'birthday' | 'meeting-note' | undefined =
    legacyState?.state === 'TodayAndTomorrowBirthday'
      ? 'birthday'
      : tab === 'reminder' || legacyState?.state === 'allReminder' || legacyState?.state === 'remindMe'
      ? 'meeting-note'
      : undefined;

  const handleRowMail = (client: QueueClient) => {
    if (rowMailAction === 'birthday') props.onBirthday?.(client);
    else if (rowMailAction === 'meeting-note') props.onDrawerAction?.('meeting-note', client);
  };

  return (
    <div
      className={`clientv2-page${selectedId !== null ? ' is-drawer-open' : ''}`}
      // The one hook the whole theme hangs off: styles.scss redefines the
      // --cv2-accent-* tokens under this attribute, so 招募模式 repaints every
      // accent on the page without a second copy of any rule.
      data-mode={recruitMode ? 'recruit' : undefined}
      ref={pageRef}
    >
      {/* The wipe sits over the page and under nothing: the app's own navy
          header is outside .clientv2-page, so it keeps its colour while the
          body repaints beneath the overlay. */}
      <RecruitModeWipe origin={wipe?.origin ?? null} to={wipe?.to ?? 'recruit'} />
      <RecruitStageMenu
        anchor={stageMenu?.anchor ?? null}
        clientId={stageMenu?.client._id ?? null}
        onClose={() => setStageMenu(null)}
        intl={props.intl}
      />
      <div className="clientv2-body">
        {/* The command bar lives inside the list column, not above the whole
            page, so the filter column beside it starts at the very top of the
            page rather than below the search row — the panel is long, and that
            row of vertical space is worth more to it than to the empty right
            end of the search bar. It also means the bar shrinks with the list
            when the drawer opens, without a rule of its own. */}
        <div className="clientv2-main">
          {/* Stays on screen in the calendar, so the page does not jump and the
              bar's other buttons keep working; only search and filter switch
              off there, since the grid counts every client with something on it
              and neither would narrow what is on screen. The term is kept and
              works again on 列表. */}
          <ClientCommandBar
            searchDisabled={calendarOpen}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            filterOpen={filterOpen}
            onFilter={handleFilterToggle}
            onFilterClose={() => setFilterOpen(false)}
            onPotentialClients={props.onPotentialClients}
            onImportClients={props.onImportClients}
            onAiSelect={props.onAiSelect}
            potentialCount={props.potentialCount}
            clientBaseLevel={clientBaseLevel}
            onClientBase={props.onClientBase && (() => props.onClientBase?.(clientBaseLevel))}
          />
          {/* 模式橫幅拿掉了 —— 整頁的琥珀色、招募分頁、四張招募統計卡,早就把
              「模式開著」講過三次;而回去的路一直都在標籤列最左邊那顆 業務 晶片
              上,它現在是藍色的,就是為了說那句話(見 ClientTabs 的 is-switch)。
              一個只為了再說一次而存在的元件,就是版面上最貴的那一種。 */}
          {/* 重點跟進 is about birthdays, renewals and clients not seen in a
              year — none of which is news about a candidate. The recruitment
              version of this card is its own slice; until then the mode simply
              does not show a card that would be answering the wrong question. */}
          {!recruitMode && (
            <TodayQueue
              entries={queue}
              adviserEmail={userEmail}
              onSelectClient={handleSelectClient}
              fillIn={factsFillIn}
              onFillIn={props.onEditFact ? handleFillIn : undefined}
              onSnoozeFillIn={handleSnoozeFillIn}
            />
          )}
          {recruitMode && (
            <RecruitSuggestions
              suggestions={recruitSuggestions}
              onSuggestion={handleSuggestion}
              onSelectClient={handleSelectClient}
              onRecontact={client => writeRecruitStage(client._id, 'contact')}
              onRemoveFromRecruitment={
                props.onEditFact ? client => props.onEditFact?.(client, 'isRecruitment', false) : undefined
              }
              adviserEmail={userEmail}
            />
          )}
          {/* No tile shows as pressed in the calendar, which ignores it; the
              filter itself is kept and shows again on the list. */}
          <StatTiles
            entering={tilesEntering}
            stats={stats}
            activeStat={calendarOpen ? null : statFilter}
            onStat={handleStat}
            recruitMode={recruitMode}
            recruitStats={recruitStats}
            activeRecruitStat={recruitStat}
            onRecruitStat={handleRecruitStat}
          />
          <ClientTabs
            activeTab={tab}
            onTab={handleTab}
            counts={tabCounts}
            sort={sort}
            onSort={handleSort}
            legacyState={legacyState?.state ?? null}
            onLegacyState={handleLegacyStateSelect}
            onRefresh={() => getClient?.()}
            refreshing={props.fetchingClients}
            calendarOpen={calendarOpen}
            onCalendar={handleCalendar}
            recruitMode={recruitMode}
            recruitAvailable={!!props.recruitmentMode}
            recruitTab={recruitTab}
            onRecruitTab={handleRecruitTab}
            recruitCounts={recruitCounts}
            onExitRecruitMode={exitRecruitMode}
            recruitSort={recruitSort}
            onRecruitSort={handleRecruitSort}
          />

          {/* Not in the calendar: its 「顯示 X / Y」 counts the list, and the grid
              ignores the search and filters that narrowed it — the banner would
              be describing something that is not on screen. It returns with
              列表, the narrowing intact. */}
          {canResetList && !calendarOpen && (
            <div
              className={`clientv2-selected-list-bar${legacyState ? ' is-legacy' : ''}${
                listIsEmpty ? ' is-empty' : ''
              }`}
              data-testid="selected-list-bar"
            >
              <div className="clientv2-selected-list-copy">
                <strong data-testid={legacyState ? 'legacy-state-label' : undefined}>
                  {props.intl?.formatMessage({
                    id: legacyState ? LEGACY_STATE_LABEL_KEYS[legacyState.state] : 'clientv2-selected-list'
                  })}
                </strong>
                {/* 「顯示 0 / 185」 and 「沒有符合…的客戶」 are the same news, so
                    the line carries whichever of the two actually informs: the
                    ratio while rows survive, the sentence once none do. It is
                    always worded as a filter result here — a narrowed list is
                    why the page is empty even if the base is empty as well. */}
                <span role="status">
                  {listIsEmpty
                    ? props.intl?.formatMessage({ id: 'clientv2-list-empty' })
                    : props.intl?.formatMessage(
                        { id: 'clientv2-selected-list-count' },
                        // 招募模式下的分母是候選人,不是整本客戶簿:這一列描述的是
                        // 螢幕上那份名單被縮到多窄,而那份名單的起點是候選人總數。
                        // 用 191 當分母,等於拿另一份名單來衡量這一份。
                        { shown: visibleClients.length, total: recruitMode ? recruitBase.length : clientData.length }
                      )}
                </span>
              </div>
              <button
                type="button"
                className="clientv2-selected-list-reset"
                data-testid={legacyState ? 'legacy-state-clear' : 'selected-list-reset'}
                aria-label={props.intl?.formatMessage({ id: 'clientv2-show-all-clients' })}
                onClick={handleShowAllClients}
              >
                <RefreshCw size={13} aria-hidden="true" />
                {props.intl?.formatMessage({ id: 'clientv2-show-all-clients' })}
              </button>
            </div>
          )}

          {props.isMovePolicy && (
            <div className="clientv2-move-policy-banner" data-testid="move-policy-banner">
              <span>{props.intl?.formatMessage({ id: 'clientv2-move-policy-banner' })}</span>
              {props.onCancelMovePolicy && (
                <button
                  type="button"
                  className="clientv2-move-policy-cancel"
                  data-testid="move-policy-cancel"
                  aria-label={props.intl?.formatMessage({ id: 'Cancel' })}
                  onClick={props.onCancelMovePolicy}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {calendarOpen ? (
            <>
              <ReminderCalendar
                month={calendarMonth}
                visibleMonth={visibleMonth}
                now={calendarNow}
                selectedDay={selectedDay}
                filter={calendarFilter}
                adviserEmail={userEmail}
                onSelectDay={setSelectedDay}
                onMonth={handleCalendarMonth}
                onFilter={setCalendarFilter}
                onExit={handleCalendar}
              />
              {/* handleSelectClient is the page's ONE way into the drawer, the
                  same one the today-queue is given. Not a second path. */}
              <ReminderDayList
                day={selectedDay}
                entries={(selectedDay && calendarMonth.byDay.get(selectedDay)) || []}
                adviserEmail={userEmail}
                onSelectClient={handleSelectClient}
                onClear={() => setSelectedDay(null)}
              />
            </>
          ) : (
            <div className="clientv2-list" ref={listRef}>
              {listIsEmpty
                ? // Only when the banner above is not already saying it — see
                  // `canResetList`. role="status" because this replaces the list in
                  // place, after an action taken elsewhere on the page (a tile, a
                  // chip, the search box) — a screen reader would otherwise get no
                  // word that the result set just emptied.
                  !canResetList && (
                    <div className="clientv2-list-empty" data-testid="list-empty" role="status">
                      <p>{props.intl?.formatMessage({ id: emptyListMessageId })}</p>
                      {/* The one moment 新增客戶 is the ONLY thing worth doing, and
                        the moment the FAB is least likely to be read as the way to
                        do it — a book with nothing in it gives its corner button no
                        context at all. Offered only when the adviser has no clients
                        AT ALL: the other wording here means a tab or a page came up
                        empty, where the answer is to look elsewhere in a book that
                        does have clients, not to add one. */}
                      {clientData.length === 0 && props.onAddClient && (
                        <button
                          type="button"
                          className="clientv2-list-empty-add"
                          data-testid="list-empty-add"
                          onClick={() => props.onAddClient?.()}
                        >
                          <Plus size={17} aria-hidden="true" />
                          {props.intl?.formatMessage({ id: 'clientv2-add-client' })}
                        </button>
                      )}
                    </div>
                  )
                : paginated.items.map(client => (
                    <ClientRow
                      key={client._id}
                      client={client}
                      selected={client._id === selectedId}
                      spotlight={client._id === spotlightId}
                      onSelect={handleSelectClient}
                      onFamilyFilter={handleRowFamilyFilter}
                      adviserEmail={userEmail}
                      selectionMode={legacyState?.state === 'ClientToSend' ? 'ClientToSend' : undefined}
                      onSelectionToggle={props.onToggleClientToSend}
                      mailAction={rowMailAction}
                      onMail={handleRowMail}
                      onQuickAction={props.onDrawerAction}
                      recruitMode={recruitMode}
                      onRecruitSurface={openRecruitSurface}
                      onRecruitStage={handleRecruitStage}
                      onRestricted={props.onRestricted}
                      // The row's potential dot shows a colour and no word, so it
                      // has to be able to explain itself: same handler and same
                      // band as the command bar's indicator and the drawer's tag.
                      onCpiInfo={props.onClientBase && (() => props.onClientBase?.(clientBaseLevel))}
                      moveSelectable={
                        Boolean(props.isMovePolicy) &&
                        movePolicyTargetErrorOf(client, props.movePolicySourceId, userEmail) === null
                      }
                      onMoveSelect={props.onSelectMovePolicyTarget}
                    />
                  ))}
              <SoftDepthScope mode={recruitMode ? 'recruit' : 'business'}>
                <ClientPagination
                  page={paginated.page}
                  total={visibleClients.length}
                  pageSize={CLIENT_PAGE_SIZE}
                  onPage={setPage}
                />
              </SoftDepthScope>
            </div>
          )}
        </div>

        <SoftDepthScope mode={recruitMode ? 'recruit' : 'business'}>
          <SmartFilterSidebar
            filters={filters}
            options={filterOptions}
            onChange={handleFilters}
            onReset={handleResetFilters}
            currency={currency}
            mobileOpen={filterOpen}
            onClose={() => setFilterOpen(false)}
            disabled={calendarOpen}
            onManageFamilies={props.onManageFamilies}
            voiceControl={props.voiceControl}
          />
        </SoftDepthScope>
      </div>

      {/* No backdrop. The drawer is non-modal on every viewport, as it always
          was on desktop: the list underneath stays live, so tapping another
          client switches the drawer instead of closing it. The phone used to
          get a full-page scrim, which made the same tap mean "close" there and
          "switch client" on desktop.
          What the scrim did provide was a way out needing no aim, and the
          drawer now carries that itself: on a phone, a tap on anything outside
          it that is NOT a client row (and not a portalled dialog) closes it,
          and on any touch device a right-swipe across the panel does. Above
          600px neither applies — the page beside the panel is fully usable
          there, and the ✕ is a precise target for a mouse. Both live in
          ClientDetailDrawer, beside the Escape handler they join. */}
      <ClientDetailDrawer
        client={selected}
        scores={selected ? scoresFor(selected) : {}}
        onClose={handleCloseDrawer}
        adviserEmail={userEmail}
        now={effectiveNow}
        onConnect={props.onConnect}
        // Same handler and same band as the client-base indicator in the
        // command bar, so the tag and the indicator open one dialog showing one
        // answer rather than two versions of it.
        onCpiInfo={props.onClientBase && (() => props.onClientBase?.(clientBaseLevel))}
        onBirthday={props.onBirthday}
        onEditPersonality={props.onEditPersonality}
        onEditFamily={props.onEditFamily}
        onEditTags={props.onEditTags}
        usedTags={usedTags}
        onEditExisting={props.onEditExisting}
        onEditTier={props.onEditTier}
        onEditFact={props.onEditFact}
        factWriteFailed={props.factWriteFailed}
        factWriteInFlight={props.factWriteInFlight}
        teamHubAvailable={props.teamHubAvailable}
        teamBotAvailable={props.teamBotAvailable}
        aiCoachAvailable={props.aiCoachAvailable}
        aiAvailable={props.aiAvailable}
        recruitmentMode={props.recruitmentMode}
        recruitListMode={recruitMode}
        recruitSurfaceRequest={recruitSurfaceRequest || undefined}
        factsRequest={factsRequest || undefined}
        onBirthdayUnlock={props.onBirthdayUnlock}
        adviserPersonality={adviserPersonality}
        signal={selectedEntry?.signal}
        onTrackingMet={props.onTrackingMet}
        onTrackingOpportunity={props.onTrackingOpportunity}
        onAction={props.onDrawerAction}
        onRestricted={props.onRestricted}
      />

      {/* Last child, so it stacks above the list without needing a z-index
          higher than the drawer's. Hidden while the drawer is open on narrow
          viewports, where the drawer covers its corner outright; from 901px up
          the CSS repositions it clear of the drawer instead (see
          `.clientv2-add-fab` in styles.scss), so it stays visible there. */}
      <ClientAddFab onAddClient={props.onAddClient} hidden={selectedId !== null && !isWideViewport} />
    </div>
  );
};

export default ClientV2;
